import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, Spinner, FormFeedback, Card, CardBody, Accordion, AccordionItem, Collapse } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { setRequireCloseModal } from '../../../../slices/screen/reducer';
import OSelect from '../../../../Components/OElements/OSelect';
import { addNewReport, updateReport } from '../../../../slices/thunks';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { getReportTemplateList } from '../../../../helpers/screen/backend_helper';
import { DatesProvider, MonthPickerInput } from '@mantine/dates';
import dayjs from 'dayjs';


const ReportForm = ({ dataReport, isEdit, handleToggleModal, isOpen }) => {
	const dispatch = useDispatch();

	const selectorReport = createSelector(
		state => state.Report,
		data => ({
			isAdding: data.isAdding,
			isAdded: data.isAdded,
			isUpdating: data.isUpdating,
			isUpdated: data.isUpdated,
			isLoadingList: data.isLoadingList,
			requireCloseModal: data.requireCloseModal,
		})
	);

	const { isAdding, isUpdating, requireCloseModal } = useSelector(selectorReport);

	const [selectedReport, setSelectedReport] = useState(null);
	const [monthValue, setMonthValue] = useState(null);

	const handleReportSelect = (optionSelected) => {
		setSelectedReport(optionSelected);
	}

	// Validation with Formik
	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			name: dataReport?.name || '',
			description: dataReport?.description || '',
			page_template_id: dataReport?.page_template_id || '',
			date_ini: dataReport?.date_ini || '',
			date_end: dataReport?.date_end || '',
			period: dataReport?.period || '',
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			description: Yup.string().required('Campo requerido'),
			page_template_id: Yup.string().required('Campo requerido'),
		}),
		validateOnChange: true,
		onSubmit: async values => {
			try {
				const data_report = {
					...values,
					...(isEdit && { _id: dataReport._id }),
					pages: selectedReport.pages,
					period: monthValue ? dayjs(monthValue).format('MMM YYYY').toUpperCase() : '',
				};

				if (isEdit) {
					await dispatch(updateReport(data_report));
				} else {
					await dispatch(addNewReport(data_report));
				}
			} catch (error) {
				console.error('Error submitting form:', error);
			} finally {
				dispatch(setRequireCloseModal(true));
			}
		},
	});


	useEffect(() => {
		if (monthValue) {
			const startOfMonth = new Date(dayjs(monthValue).startOf('month').format('YYYY-MM-DD'));
			const endOfMonth = new Date(dayjs(monthValue).endOf('month').format('YYYY-MM-DD'));

			if (validation.values.date_ini !== startOfMonth || validation.values.date_end !== endOfMonth) {
				validation.setFieldValue('date_ini', startOfMonth, false);
				validation.setFieldValue('date_end', endOfMonth, false);
			}
		}
	}, [monthValue]);  // Solo se ejecutará cuando monthValue cambie

	useEffect(() => {
		if (isOpen && requireCloseModal) {
			dispatch(setRequireCloseModal(false));
			handleToggleModal();
		}
	}, [requireCloseModal, dispatch, handleToggleModal, isOpen]);

	return (
			<Form
				className='tablelist-form'
				onSubmit={e => {
					e.preventDefault();
					validation.handleSubmit();
					return false;
				}}
			>
				<ModalBody className='modal-body'>
					<Row className='g-3'>
						<Col lg={6}>
							<Label for='name' className='form-label'>
								Nombre del reporte
							</Label>
							<Input
								name='name'
								id='name'
								className='form-control'
								placeholder='Ingrese el nombre del reporte'
								type='text'
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.name || ''}
								invalid={validation.touched.name && validation.errors.name ? true : false}
							/>
							{validation.touched.name && validation.errors.name ? (
								<FormFeedback type='invalid'>{validation.errors.name}</FormFeedback>
							) : null}
						</Col>

						<Col sm={2}>
							<DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
								<MonthPickerInput
									key={`period`}
									label="Periodo del reporte"
									placeholder="Selecciona un mes"
									value={monthValue}
									onChange={setMonthValue}
									clearable
								/>
							</DatesProvider>
						</Col>

						<Col md={4}>
							<OSelect
								key={`page_template_id`}
								label='Plantilla de Reporte'
								id='page_template_id'
								name='page_template_id'
								value={selectedReport}
								onSelect={handleReportSelect}
								requestApi={() => getReportTemplateList()}
								validation={validation}
							/>
						</Col>
						<Col lg={12}>
							<Label for='description' className='form-label'>
								Descripción
							</Label>
							<Input
								name='description'
								id='description'
								className='form-control'
								placeholder='Ingrese el nombre del reporte'
								type='textarea'
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.description || ''}
								invalid={validation.touched.description && validation.errors.description ? true : false}
							/>
							{validation.touched.description && validation.errors.description ? (
								<FormFeedback type='invalid'>{validation.errors.description}</FormFeedback>
							) : null}
						</Col>
					</Row>
				</ModalBody>
				<div className='modal-footer'>
					<div className='hstack gap-2 justify-content-end'>
						<Button
							type='button'
							onClick={() => {
								handleToggleModal();
							}} className='btn-danger btn-label'>
							<i className="icon-x label-icon"></i>
							Cancelar
						</Button>
						<button
							type='submit'
							className='btn btn-label btn-primary'
							id='add-btn'
							disabled={isAdding || isUpdating}
						>
							{isAdding || isUpdating ? (
								<i className='label-icon'><Spinner size='sm' /></i>
							) : <i className="ri-save-3-line label-icon"></i>}
							{isEdit ? 'Editar' : 'Crear'} Reporte
						</button>
					</div>
				</div>
			</Form>
	);
};

export default ReportForm;
